import { Injectable } from '@angular/core';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { map, delay, tap, concatMap } from 'rxjs/operators';
import { UserAuth } from './user';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as dayjs from 'dayjs';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { ObjetoEnum, PermissaoEnum } from './objeto.enum';
import { NotificationService } from '../services/notification.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoaderService } from 'src/app/shared/components/loader/loader.service';
import { environment } from 'src/environments/environment';
import { UsuarioService } from '../services/usuario.service';
import { ApiService } from '@core/services/api.service';
import { RoteiroPorEmailService } from '@modules/roteiro-por-email/roteiro-por-email.service';

const routes = {
  login: `auth/tokens`,
  getUserData: `auth/prefs`,
  revokeToken: `auth/tokens/revoke`,
  salvarSenha: `auth/salvarSenha`,
  recuperarSenha: `auth/recuperarSenha`,
  recuperarLogin: `auth/recuperarLogin`,
  urlAcesso: window.location.origin + '/api/auth/',
  consultCodigo: (codigo: string) => `/TempCodeGenerator/codigo/${codigo}`
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<UserAuth>;
  private loggedIn = new BehaviorSubject<boolean>(false);
  private loggedOut = false;

  constructor(private apiService: ApiService,
    private localStorageService: LocalStorageService,
    private readonly router: Router,
    private loaderService: LoaderService,
    private usuarioService: UsuarioService,
    private roleService: NgxRolesService
  ) {
    this.currentUserSubject = new BehaviorSubject<UserAuth>(JSON.parse(localStorageService.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
    this.loggedIn = new BehaviorSubject<boolean>(localStorageService.getItem('token') != undefined);
  }

  public get isLoggedIn() {
    return this.loggedIn.asObservable().pipe(delay(0));
  }

  public get currentUserValue(): UserAuth {
    return this.currentUserSubject.value;
  }

  public get isLoged(): boolean {
    return this.loggedIn.value;
  }

  isLoggedOut(): boolean {
    return this.loggedOut;
  }

  setLoggedOut(loggedOut: boolean): void {
    this.loggedOut = loggedOut;
  }

  login(login: string, senha: string) {
    const dataInfo = { login, senha };
    this.setLoggedOut(false);

    return this.apiService.post<any>(routes.login, dataInfo)
      .pipe(tap(res => {
        return res;
      }),
        concatMap((res) => {
          if (res.token) {
            return this.getUserData(res.token).pipe(map(data => {
              if (!res.expires_in) {
                this.salvarToken(res, data);
              }
              return { ...data, ...res };
            }));
          }
          return of(res);
        }),
        map((data: UserAuth) => {
          return data;
        }));
  }

  salvarToken(res: any, data: any) {
    const helper = new JwtHelperService();
    const dados = helper.decodeToken(res.token);
    const dadosUsuario = { ...dados, ...data };
    this.localStorageService.setItem('token', JSON.stringify(res.token));
    const dataExpiracao = dayjs(dados.created).add(dados.exp * 1000, 'millisecond').toDate();
    const expire = dayjs(dataExpiracao).format();
    this.localStorageService.setItem('sessaoAtiva', 'true')
    dadosUsuario.expires_in = expire;
    this.setCurrentUser(dadosUsuario);
    this.loggedIn.next(true);
  }

  roteiroCodigoAcesso(codigo: string) {
    return this.apiService.get<any>(routes.consultCodigo(codigo))
    // return this.apiService.get<any>(routes.consultCodigo(codigo)).pipe(tap(res => {
    //   if (this.localStorageService.getItem('currentUserRoteiroEmail')) {
    //     this.loggedIn.next(true);
    //   }
    // }))
  }

  logout(setReturnUrl?: boolean) {
    if (!this.router.url.startsWith('/login')) {
      const logoutCall = () => {
        this.clearLocalStorage();
        this.currentUserSubject.next(null);
        this.loggedIn.next(false);
        this.loaderService.hide();

        this.setLoggedOut(true);

        // const redirectUrl = setReturnUrl ? { queryParams: { returnUrl: this.router.url, msg: message } } : '/login';
        const redirectUrl = '/login';

        this.revokeToken().pipe(
          tap(() => {
            this.router.navigate([redirectUrl])
            this.loaderService.hide();
          })
        ).subscribe({
          error: () => {
            this.router.navigate([redirectUrl])
            this.loaderService.hide();
          }
        });
      };

      logoutCall();
    }
  }

  private clearLocalStorage() {
    this.localStorageService.removeItem('currentUser');
    this.localStorageService.removeItem('token');
    this.localStorageService.removeItem('expiration_token');
    this.localStorageService.removeItem('currentUserRoteiroEmail');
    this.localStorageService.removeItem('sessaoAtiva');
    this.localStorageService.removeItem('permissions');
  }

  refreshToken() {
    let params = new HttpParams();
    params = params.append('ignoreLoading', 'true');
    params = params.append('ignoreError', 'true');
    return this.apiService.get<any>(`${environment.refreshToken}`, undefined, params);
  }

  getUserData(token?: string) {
    if (token) {
      const header = new HttpHeaders().append('Authorization', token);
      return this.apiService.get<any>(routes.getUserData, true, undefined, header);
    }

    return this.apiService.get<any>(routes.getUserData);
  }

  revokeToken() {
    return this.apiService.get<any>(routes.revokeToken)
  }

  salvarSenha(obj: any, token: string) {
    let header = new HttpHeaders();
    header = header.append('Authorization', token);
    return this.apiService.put<any>(routes.salvarSenha, obj, false, undefined, header);
  }

  alterarSenha(obj: any) {
    return this.apiService.put<any>(routes.salvarSenha, obj, false, undefined, undefined);
  }

  recuperarSenha(login: string) {
    let params = new HttpParams();
    params = params.append('login', login);
    return this.apiService.get<any>(routes.recuperarSenha, undefined, params);
  }

  recuperarLogin(email: string) {
    return this.apiService.post<any>(routes.recuperarLogin, email);
  }

  setCurrentUser(dadosUsuario: any) {
    this.localStorageService.setItem('currentUser', JSON.stringify(dadosUsuario));
    this.currentUserSubject.next(dadosUsuario);
  }

  atualizarCurrentUser(dadosUsuario?: any) {
    const dados = dadosUsuario ?? JSON.parse(this.localStorageService.getItem('currentUser'));

    if (dados) {
      this.usuarioService.consultarPorId(dados.id).subscribe({
        next: (res) => {
          const resAdicional = { ...res };
          delete resAdicional.grupo;
          const novosDadosUsuario = { ...dados, resAdicional };
          this.localStorageService.setItem('currentUser', JSON.stringify(novosDadosUsuario));
          this.currentUserSubject.next(novosDadosUsuario);
          this.addRolesAcesso(novosDadosUsuario)
        },
        error: () => null
      });
    }
  }

  mustChangePassword() {
    if (this.loggedIn && this.currentUserValue.mustChangePassword) {
      this.router.navigate(['/senha-expirada']);
    }
  }

  addRolesAcesso(dadosUsuario: any) {
    const permissions: string[] = [];

    dadosUsuario?.grupo?.idsPermissaoXObjeto?.forEach((permission: any) => {
      permissions.push(permission);
    });

    const role = dadosUsuario?.grupo?.descricao;
    const permissionsSemRepeat = permissions.filter((permission, index) => permissions.indexOf(permission) === index);

    const permissaoDashboard = <any>{
      objeto: ObjetoEnum.TelaDashboard,
      permissao: PermissaoEnum.Acessar
    }

    permissionsSemRepeat.push(permissaoDashboard)
    this.roleService.addRole(role, permissionsSemRepeat);
  }
}
